/**
 * ATL-1590 — the working-hours records the Cash Flow report's «Зарплаты» accrue for a period.
 *
 * WHY THIS EXISTS
 *
 * `PaymentStatsScreen.getDebitCredit` used to read the hourly/daily accruals with
 *
 *   getWorkingHoursOnDateRange(range, true, store)
 *
 * against the signature `(range, userId = null, flat = false, store = null)`. That is userId=`true`,
 * flat=<the store id>, store=`null`, so the query became `where('employeeID', '==', true)` and
 * returned nothing, ever. Every hourly and daily salary was missing from «Начислено», from each
 * employee's card, and from «Предстоящие расходы» / «Ожидаемый баланс»; only monthly accruals were
 * counted. Measured on org uV2Urt7u6YuXDRu2TBge, 2–8 Oct 2026: seven `day` records of 7 000 plus
 * 84 880.6 of bonuses, and the report printed 0.
 *
 * Two traps sit next to the obvious one-argument fix, and this module owns both:
 *
 * 1. A MONTHLY record matches BOTH reads: it has a `date` inside the period AND a `startDate` the
 *    monthly read selects (measured on UNDERWOOD DECOR, September 2026: all 16 of its date-range
 *    records were also returned by the monthly read). Correcting the call alone would therefore
 *    double-count every monthly salary. Each salary type gets exactly one owner instead: monthly
 *    records come only from `getMonthlyWorkingHoursOnDateRange` (which also picks up a month that
 *    began before the period), everything else only from the date-range read.
 *
 * 2. The store chooser offers «Все» as the literal string `'all'`. Both getters add
 *    `where('store', '==', store)` for any truthy store, so `'all'` matched no record at all — which
 *    is also why the monthly accruals already read 0 under «Все». `'all'` means "no store clause".
 *    Every query shape this issues, with and without the store clause, is served by an existing
 *    Firestore index (probed read-only against prod 2026-10-08).
 *
 * Both getters read Firestore in either read mode, deliberately: working hours are written to
 * Firestore first, and `getWorkingHoursOnDateRange` has never branched on `shouldUseSupabase()`
 * (see `TOGGLED_SOURCE_OPTIONS` in utils/validationFunctions.js).
 */
import {
  getWorkingHoursOnDateRange,
  getMonthlyWorkingHoursOnDateRange,
  type DateRange,
  type WorkingHoursInfo,
} from '../../global/Firebase/workingHours'

/** The report's store selection — a store id, `'all'`, or nothing chosen yet. */
export type ReportStore = string | null | undefined

/**
 * The store clause the working-hours getters should apply for the report's selection: the store id
 * itself, or `null` (every store) for «Все» and for no selection. Both getters treat a falsy store
 * as "do not filter by store".
 */
export const workingHoursStoreFilter = (store: ReportStore): string | null =>
  !store || store === 'all' ? null : store

export interface SalaryAccruals {
  /** Hourly and daily (and untyped legacy) records whose `date` falls in the period. */
  datedWorkingHours: WorkingHoursInfo[]
  /** Monthly records overlapping the period, as `getMonthlyWorkingHoursOnDateRange` selects them. */
  monthlyWorkingHours: WorkingHoursInfo[]
}

/**
 * Reads every working-hours record the period accrues, each exactly once: `range` is the report's
 * already timezone-adjusted range, `store` the report's selection.
 */
export const fetchSalaryAccruals = async (range: DateRange, store: ReportStore): Promise<SalaryAccruals> => {
  const storeFilter = workingHoursStoreFilter(store)
  const [dated, monthlyWorkingHours] = await Promise.all([
    // `null` userId: every employee. `flat` is true, so the getter returns WorkingHoursInfo[]
    // rather than records grouped by day; its declared return type is the union of both shapes
    // (the same cast `countEmployeeBonuses` makes on it).
    getWorkingHoursOnDateRange(range, null, true, storeFilter) as Promise<WorkingHoursInfo[]>,
    getMonthlyWorkingHoursOnDateRange(range, storeFilter),
  ])
  return {
    // Kept client-side on purpose: a Firestore `salaryType != 'month'` / `in ['hour','day']` clause
    // would also drop records that have no `salaryType` at all, which this report has always
    // accrued as dated records. The cost is one extra read per monthly record in the period.
    datedWorkingHours: dated.filter(wh => wh.salaryType !== 'month'),
    monthlyWorkingHours,
  }
}

/** One employee's accrual for the period, as the report's employee card and totals read it. */
export interface EmployeeAccrual {
  totalDebit: number
  totalCredit: number
  workingHours: WorkingHoursInfo[]
  salaryTransactions: unknown[]
}

/**
 * The period's «Начислено»: the whole-report `totalDebit` plus one `EmployeeAccrual` per
 * `employeeID`, each record contributing `amount + employeeBonusAmount` exactly once. Credits start
 * at 0 — the screen's transaction listener books payments onto this same object afterwards.
 *
 * This is the arithmetic `PaymentStatsScreen.processEmployeeSalaries` always did, moved here so the
 * figure the merchant sees is the figure the ATL-1590 regression test asserts. Each employee's entry
 * is created once and appended to, rather than copied per record as it used to be: hourly staff now
 * contribute one record per shift, and the copy was quadratic in that count.
 */
export const accrueEmployeeSalaries = (
  { datedWorkingHours, monthlyWorkingHours }: SalaryAccruals,
): { totalDebit: number; totalCredit: number } & Record<string, EmployeeAccrual | number> => {
  const byEmployee: Record<string, EmployeeAccrual> = {}
  let totalDebit = 0
  for (const wh of [...datedWorkingHours, ...monthlyWorkingHours]) {
    // Keyed exactly as before (`String(undefined)` included), so a record without an employee
    // still surfaces as its own line rather than vanishing from the total.
    const key = String(wh.employeeID)
    const entry = byEmployee[key] ?? (byEmployee[key] = { totalDebit: 0, totalCredit: 0, workingHours: [], salaryTransactions: [] })
    const accrued = (wh.amount || 0) + (wh.employeeBonusAmount || 0)
    entry.totalDebit += accrued
    entry.workingHours.push(wh)
    totalDebit += accrued
  }
  return { ...byEmployee, totalDebit, totalCredit: 0 }
}
